import { useState, useEffect } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import toast from 'react-hot-toast';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import {
  IconChevronDown, IconChevronUp, IconDownload,
  IconCheck, IconX, IconSend, IconAlertCircle, IconRefresh, IconCalendar, IconPlus, IconTrash
} from '@tabler/icons-react';
import {
  useKPISections,
  useCreateKPISection,
  useUpdateKPISection,
  useSelfRateSection,
  useSubmitAllSections,
  usePendingReviews,
  useReviewSection,
  useDownloadScorecard,
} from '../../hooks/usePerformance';

// Define the 7 required section names
const REQUIRED_SECTIONS = [
  'Business Development',
  'Client and Stakeholder Management',
  'Continuous Learning and Capacity Development',
  'Leadership and Ownership',
  'Problem Solving and Critical Thinking',
  'Quality of Output',
  'Timeliness and Productivity',
];

export default function KPIManagement() {
  const orgId = localStorage.getItem('orgId') || '';
  const userRole = localStorage.getItem('userRole') || 'employee';
  const employeeId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.employeeId || ''; }
    catch { return ''; }
  })();

  const [selectedCycle, setSelectedCycle] = useState(() => localStorage.getItem('kpi_selectedCycle') || '');
  const [cycleName, setCycleName] = useState(() => localStorage.getItem('kpi_cycleName') || '');
  const [cycleStartDate, setCycleStartDate] = useState(() => localStorage.getItem('kpi_cycleStartDate') || '');
  const [cycleEndDate, setCycleEndDate] = useState(() => localStorage.getItem('kpi_cycleEndDate') || '');
  const [expandedSections, setExpandedSections] = useState([]);
  const [activeTab, setActiveTab] = useState('employee');

  // Modal States
  const [confirmSubmitModalOpen, setConfirmSubmitModalOpen] = useState(false);
  const buildEmptySections = () => {
    const empty = {};
    REQUIRED_SECTIONS.forEach(name => {
      empty[name] = {
        id: null,
        status: 'draft',
        manager_comment: null,
        items: [
          { title: '', description: '', weight: 50, means_of_verification: '', self_rating: '', self_comment: '' },
          { title: '', description: '', weight: 50, means_of_verification: '', self_rating: '', self_comment: '' },
        ],
      };
    });
    return empty;
  };

  const [kpiSections, setKpiSections] = useState(buildEmptySections);

  const { data: sectionsData, isLoading: loadingSections, refetch: refetchSections } = useKPISections(orgId, cycleName, cycleStartDate);
  const { data: pendingReviewsData } = usePendingReviews(orgId);
  const createSection = useCreateKPISection();
  const updateSection = useUpdateKPISection();
  const selfRateSection = useSelfRateSection();
  const submitAll = useSubmitAllSections();
  const reviewSection = useReviewSection();
  const downloadScorecard = useDownloadScorecard();

  // When cycle data loads, populate sections with existing statuses
  useEffect(() => {
    if (!sectionsData) return;
    const incoming = Array.isArray(sectionsData) ? sectionsData : sectionsData?.results || [];
    if (incoming.length === 0) return;

    setKpiSections(prev => {
      const updated = { ...prev };
      incoming.forEach(section => {
        const loadedItems = section.items?.length
          ? section.items.map(({ id, title, description, weight, means_of_verification, self_rating, self_comment, manager_rating, manager_comment }) => ({
            id,
            title, description,
            weight: Number(weight) || 50,
            means_of_verification,
            self_rating: self_rating ?? '',
            self_comment: self_comment ?? '',
            manager_rating: manager_rating ?? null,
            manager_comment: manager_comment ?? null,
          }))
          : prev[section.section_name]?.items || [
            { title: '', description: '', weight: 50, means_of_verification: '', self_rating: '', self_comment: '' },
            { title: '', description: '', weight: 50, means_of_verification: '', self_rating: '', self_comment: '' },
          ];
        updated[section.section_name] = {
          id: section.id,
          status: section.status,
          manager_comment: section.manager_comment,
          items: loadedItems,
          originalItems: loadedItems.map(i => ({ ...i })),
        };
      });
      return updated;
    });
  }, [sectionsData]);

  const toggleSection = (section) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  const handleKPIChange = (sectionName, itemIndex, field, value) => {
    const parsed = field === 'weight' ? (value === '' ? '' : Number(value)) : value;
    setKpiSections(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        items: prev[sectionName].items.map((item, idx) =>
          idx === itemIndex ? { ...item, [field]: parsed } : item
        )
      }
    }));
  };

  const handleAddKPIItem = (sectionName) => {
    setKpiSections(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        items: [
          ...prev[sectionName].items,
          { title: '', description: '', weight: 50, means_of_verification: '', self_rating: '', self_comment: '' },
        ],
      },
    }));
  };

  const handleRemoveKPIItem = (sectionName, itemIndex) => {
    setKpiSections(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        items: prev[sectionName].items.filter((_, idx) => idx !== itemIndex),
      },
    }));
  };

  // Build item payload. includeId=true keeps the item's id (needed for PUT/PATCH updates).
  // self_rating and self_comment are only sent when the section is accepted.
  const cleanItems = (items, includesSelfAssessment = false, includeId = false) =>
    items.map(({ id, title, description, weight, means_of_verification, self_rating, self_comment }) => {
      const item = {
        title,
        description,
        weight: Number(weight) || 0,
        means_of_verification,
      };
      if (includeId && id) item.id = id;
      if (includesSelfAssessment) {
        item.self_rating = self_rating !== '' ? Number(self_rating) : null;
        item.self_comment = self_comment || '';
      }
      return item;
    });

  const handleSaveSection = async (sectionName) => {
    const section = kpiSections[sectionName];
    const isAccepted = section.status === 'accepted';

    // ── Self-rate path (section accepted by manager) ──────────────────────────
    if (isAccepted && section.id) {
      const selfRatePayload = {
        ratings: section.items
          .filter(item => item.id)
          .map(item => ({
            item_id: item.id,
            self_rating: item.self_rating !== '' ? parseInt(item.self_rating, 10) : null,
            self_comment: item.self_comment || '',
          })),
      };
      try {
        await selfRateSection.mutateAsync({ sectionId: section.id, data: selfRatePayload, orgId });
        toast.success(`Self assessment for "${sectionName}" saved`);
      } catch (error) {
        console.error('Error saving self assessment:', error);
      }
      return;
    }

    // ── Regular save / update path ────────────────────────────────────────────
    const payload = {
      org: orgId,
      employee: employeeId,
      cycle_name: cycleName,
      cycle_start_date: cycleStartDate,
      cycle_end_date: cycleEndDate,
      section_name: sectionName,
      items: cleanItems(section.items, false),
    };
    try {
      let result;
      if (section.id) {
        // PUT — always send all items so backend weight-sum validation passes
        result = await updateSection.mutateAsync({
          id: section.id,
          data: { ...payload, items: cleanItems(section.items, isAccepted, true) },
          orgId,
        });
      } else {
        // New section — create it
        result = await createSection.mutateAsync({ data: payload, orgId });
      }
      // API may return a single object or an array — normalise to object
      const saved = Array.isArray(result) ? result[0] : result;
      if (saved?.id) {
        // Map items from response (they now have backend IDs) so subsequent updates
        // can diff correctly and include item IDs in the payload
        const savedItems = saved.items?.length
          ? saved.items.map(({ id, title, description, weight, means_of_verification, self_rating, self_comment, manager_rating, manager_comment }) => ({
            id,
            title, description,
            weight: Number(weight) || 0,
            means_of_verification,
            self_rating: self_rating ?? '',
            self_comment: self_comment ?? '',
            manager_rating: manager_rating ?? null,
            manager_comment: manager_comment ?? null,
          }))
          : null;

        setKpiSections(prev => ({
          ...prev,
          [sectionName]: {
            ...prev[sectionName],
            id: saved.id,
            status: saved.status || 'draft',
            ...(savedItems && {
              items: savedItems,
              originalItems: savedItems.map(i => ({ ...i })),
            }),
          }
        }));
        toast.success(`${sectionName} saved successfully`);
      }
    } catch (error) {
      console.error('Error saving section:', error);
    }
  };

  const handleSubmitAll = async () => {
    setConfirmSubmitModalOpen(true);
  };

  const confirmSubmitAll = async () => {
    try {
      await submitAll.mutateAsync({
        data: {
          org: orgId,
          employee: employeeId,
          cycle_name: cycleName,
          cycle_start_date: cycleStartDate,
          cycle_end_date: cycleEndDate,
        },
      });
      toast.success(`All sections for cycle "${cycleName}" submitted for review`);
      setConfirmSubmitModalOpen(false);
      refetchSections();
    } catch (error) {
      console.error('Error submitting sections:', error);
    }
  };


  const handleAcceptSection = async (review) => {
    try {
      await reviewSection.mutateAsync({
        sectionId: review.id,
        orgId,
        data: {
          action: 'accept',
          org: review.org,
          employee: review.employee,
          cycle_name: review.cycle_name,
          cycle_start_date: review.cycle_start_date,
          cycle_end_date: review.cycle_end_date,
          section_name: review.section_name,
        },
      });
      toast.success(`Section "${review.section_name}" for ${review.employee_name} accepted`);
    } catch (error) {
      console.error('Error accepting section:', error);
    }
  };

  const handleRejectSection = async (review) => {
    const comment = prompt(`Provide feedback for rejecting "${review.section_name}" (${review.employee_name}):`);
    if (!comment) return;
    try {
      await reviewSection.mutateAsync({
        sectionId: review.id,
        orgId,
        data: {
          action: 'reject',
          comment,
          org: review.org,
          employee: review.employee,
          cycle_name: review.cycle_name,
          cycle_start_date: review.cycle_start_date,
          cycle_end_date: review.cycle_end_date,
          section_name: review.section_name,
        },
      });
      toast.success(`Section "${review.section_name}" for ${review.employee_name} rejected with feedback`);
    } catch (error) {
      console.error('Error rejecting section:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-600';
      case 'submitted': return 'bg-blue text-white';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const noCycleSelected = !cycleName || !cycleStartDate || !cycleEndDate;
  const allSectionsSaved = REQUIRED_SECTIONS.every(name => !!kpiSections[name]?.id);
  const allSectionsAccepted = REQUIRED_SECTIONS.every(name => kpiSections[name]?.status === 'accepted');

  return (
    <div className="flex flex-col gap-20 min-h-screen mb-40">
      <Breadcrumb title="KPI Management" path="Performance Management" />

      {/* Tab Navigation */}
      <div className="mb-20 flex gap-10 border-b border-border">
        <button
          onClick={() => setActiveTab('employee')}
          className={`px-20 py-10 text-sm font-bold transition-all ${activeTab === 'employee'
            ? 'border-b-2 border-primary text-primary'
            : 'text-text-secondary hover:text-text-primary'
            }`}
        >
          My KPIs
        </button>
        {(userRole === 'manager' || userRole === 'both') && (
          <button
            onClick={() => setActiveTab('manager')}
            className={`px-20 py-10 text-sm font-bold transition-all relative ${activeTab === 'manager'
              ? 'border-b-2 border-primary text-primary'
              : 'text-text-secondary hover:text-text-primary'
              }`}
          >
            Pending Reviews
            {pendingReviewsData && pendingReviewsData.length > 0 && (
              <span className="absolute -top-5 -right-5 bg-red-500 text-white text-xs rounded-full w-18 h-18 flex items-center justify-center">
                {pendingReviewsData.length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Employee View: My KPIs */}
      {activeTab === 'employee' && (
        <div className="bg-bg-dark rounded-lg border border-border overflow-hidden shadow-sm">
          {/* Top Action Bar */}
          <div className="px-20 py-15 border-b border-border flex flex-wrap gap-15 justify-between items-end bg-bg-light/30">
            <div className="flex flex-wrap items-end gap-10">
              {/* Cycle Dropdown */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase block mb-3">Performance Cycle</label>
                <div className="relative min-w-[220px]">
                  <select
                    value={selectedCycle}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedCycle(val);
                      localStorage.setItem('kpi_selectedCycle', val);
                      const cycleNames = {
                        'annual': 'Annual Performance Review',
                      };
                      const name = cycleNames[val] || '';
                      setCycleName(name);
                      localStorage.setItem('kpi_cycleName', name);
                    }}
                    className="w-full px-12 py-10 rounded-lg border border-border focus:border-primary focus:ring-2 focus:ring-primary/10 bg-bg-light text-text-primary appearance-none text-sm"
                  >
                    <option value="">Select Performance Cycle...</option>
                    <option value="annual">Annual Performance Review</option>
                  </select>
                  <IconChevronDown size={18} className="absolute right-12 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
                </div>
              </div>

              {/* Start Date */}
              <div>
                <label className="text-[10px] font-bold text-text-secondary uppercase block mb-3">Start Date</label>
                <div className="relative">
                  <IconCalendar size={14} className="absolute left-10 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
                  <input
                    type="date"
                    value={cycleStartDate}
                    onChange={(e) => {
                      setCycleStartDate(e.target.value);
                      localStorage.setItem('kpi_cycleStartDate', e.target.value);
                    }}
                    className="pl-28 pr-10 py-10 rounded-lg border border-border focus:border-primary focus:ring-2 focus:ring-primary/10 bg-bg-light text-text-primary text-sm"
                  />
                </div>
              </div>

              {/* End Date */}
              <div>
                <label className="text-[10px] font-bold text-text-secondary uppercase block mb-3">End Date</label>
                <div className="relative">
                  <IconCalendar size={14} className="absolute left-10 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
                  <input
                    type="date"
                    value={cycleEndDate}
                    onChange={(e) => {
                      setCycleEndDate(e.target.value);
                      localStorage.setItem('kpi_cycleEndDate', e.target.value);
                    }}
                    className="pl-28 pr-10 py-10 rounded-lg border border-border focus:border-primary focus:ring-2 focus:ring-primary/10 bg-bg-light text-text-primary text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-10">
              <button
                onClick={() => refetchSections()}
                disabled={!cycleName || loadingSections}
                title="Load your current section statuses from the server"
                className="flex items-center gap-8 px-10 py-3 rounded border border-border text-[11px] font-bold text-text-secondary hover:text-text-primary bg-bg-light hover:bg-bg-light/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <IconRefresh size={14} className={loadingSections ? 'animate-spin' : ''} />
                {loadingSections ? 'Loading...' : 'Load Status'}
              </button>
              <button
                onClick={() => downloadScorecard.mutate({ orgId, cycleName, cycleStartDate })}
                disabled={downloadScorecard.isPending || noCycleSelected}
                title={noCycleSelected ? 'Select a cycle and start date first' : 'Download scorecard PDF'}
                className="flex items-center gap-8 px-15 py-3 rounded bg-[#d32f2f] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#b71c1c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconDownload size={16} />
                {downloadScorecard.isPending ? 'Downloading...' : 'Export PDF'}
              </button>
            </div>
          </div>

          {/* Info Banner */}
          <div className="px-20 py-12 border-b border-border bg-blue-50 flex items-center gap-10">
            <IconAlertCircle size={16} className="text-blue-600" />
            <p className="text-[12px] font-medium text-blue-800">
              {noCycleSelected
                ? 'Select a performance cycle above to view or fill in your KPIs.'
                : 'Fill in each section and click Save Section. Once all sections are saved, click Submit All Sections to send to your manager.'}
            </p>
          </div>

          {/* Column Headers */}
          <div className="bg-bg-dark">
            <div className="grid grid-cols-12 px-20 py-10 bg-bg-light/30 text-[11px] font-bold text-text-secondary uppercase tracking-widest border-b border-border">
              <div className="col-span-11 flex justify-between px-10">
                <span className="w-1/3">Section Header</span>
                <span>Status</span>
              </div>
              <div className="col-span-1"></div>
            </div>

            {/* KPI Sections */}
            <div className="divide-y divide-border">
              {REQUIRED_SECTIONS.map((sectionName, idx) => {
                const section = kpiSections[sectionName] || { items: [], status: 'draft' };
                const isExpanded = expandedSections.includes(sectionName);
                const isAccepted = section.status === 'accepted';
                const isSubmitted = section.status === 'submitted';
                const isSaved = !!section.id;
                // Fields are read-only when submitted (pending review) or accepted (for main fields)
                const isReadOnly = isSubmitted || isAccepted;
                // Lock self-assessment once manager has rated any item
                const isManagerRated = section.items.some(item => item.manager_rating !== null && item.manager_rating !== undefined);

                return (
                  <div key={idx} className="group">
                    <div
                      onClick={() => toggleSection(sectionName)}
                      className="grid grid-cols-12 px-20 py-15 items-center cursor-pointer hover:bg-bg-light/50 transition-colors"
                    >
                      <div className="col-span-11 flex justify-between items-center px-10">
                        <div className="flex items-center gap-10">
                          {isExpanded ? (
                            <IconChevronUp size={14} className="text-text-secondary" />
                          ) : (
                            <IconChevronDown size={14} className="text-text-secondary" />
                          )}
                          <span className="text-[13px] font-bold text-text-primary">{sectionName}</span>
                          {isSaved && (
                            <span className="flex items-center gap-4 text-[10px] font-bold text-green-600 bg-green-500/10 px-8 py-3 rounded-full">
                              <IconCheck size={10} />
                              Saved
                            </span>
                          )}
                        </div>
                        <span className={`text-[11px] font-bold px-10 py-4 rounded ${getStatusColor(section.status)}`}>
                          {section.status?.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-30 pb-30 bg-bg-light/20">
                        {/* Manager Comment (if rejected) */}
                        {section.status === 'rejected' && section.manager_comment && (
                          <div className="mb-15 p-15 bg-red-500/10 border border-red-500/20 rounded">
                            <p className="text-xs font-bold text-red-500 mb-5">Manager Feedback:</p>
                            <p className="text-sm text-red-500">{section.manager_comment}</p>
                          </div>
                        )}

                        <div>
                          {/* Column labels */}
                          <div className="grid grid-cols-12 gap-15 mb-10 px-10">
                            <div className="col-span-3">
                              <label className="text-[10px] font-bold text-text-secondary uppercase">KPI Title</label>
                            </div>
                            <div className="col-span-1 text-center">
                              <label className="text-[10px] font-bold text-text-secondary uppercase">Weight %</label>
                            </div>
                            <div className="col-span-4">
                              <label className="text-[10px] font-bold text-text-secondary uppercase">Description</label>
                            </div>
                            <div className="col-span-4">
                              <label className="text-[10px] font-bold text-text-secondary uppercase">Means Of Verification</label>
                            </div>
                          </div>

                          {/* KPI Items */}
                          {section.items.map((kpi, kIdx) => (
                            <div key={kIdx} className="mb-15">
                              <div className="border border-gray-200 rounded bg-white relative">
                                {/* Main fields row */}
                                <div className="grid grid-cols-12 gap-15 px-10 py-10">
                                  <div className="col-span-3">
                                    <textarea
                                      readOnly={isReadOnly}
                                      rows={4}
                                      className={`w-full text-[12px] border border-border rounded p-10 resize-none outline-none ${!isReadOnly ? 'bg-bg-dark text-text-primary focus:border-primary focus:ring-1 focus:ring-primary' : 'bg-bg-light text-text-secondary cursor-not-allowed'}`}
                                      value={kpi.title}
                                      onChange={(e) => handleKPIChange(sectionName, kIdx, 'title', e.target.value)}
                                      placeholder="Enter KPI title..."
                                    />
                                  </div>
                                  <div className="col-span-1">
                                    <input
                                      readOnly={isReadOnly}
                                      type="number"
                                      min="0"
                                      max="100"
                                      className={`w-full text-[12px] border border-border rounded p-10 text-center outline-none ${!isReadOnly ? 'bg-bg-dark text-text-primary focus:border-primary focus:ring-1 focus:ring-primary' : 'bg-bg-light text-text-secondary cursor-not-allowed'}`}
                                      value={kpi.weight}
                                      onChange={(e) => handleKPIChange(sectionName, kIdx, 'weight', e.target.value)}
                                    />
                                  </div>
                                  <div className="col-span-4">
                                    <textarea
                                      readOnly={isReadOnly}
                                      rows={4}
                                      className={`w-full text-[12px] border border-border rounded p-10 resize-none outline-none ${!isReadOnly ? 'bg-bg-dark text-text-primary focus:border-primary focus:ring-1 focus:ring-primary' : 'bg-bg-light text-text-secondary cursor-not-allowed'}`}
                                      value={kpi.description}
                                      onChange={(e) => handleKPIChange(sectionName, kIdx, 'description', e.target.value)}
                                      placeholder="Enter detailed description..."
                                    />
                                  </div>
                                  <div className="col-span-4">
                                    <textarea
                                      readOnly={isReadOnly}
                                      rows={4}
                                      className={`w-full text-[12px] border border-border rounded p-10 resize-none outline-none ${!isReadOnly ? 'bg-bg-dark text-text-primary focus:border-primary focus:ring-1 focus:ring-primary' : 'bg-bg-light text-text-secondary cursor-not-allowed'}`}
                                      value={kpi.means_of_verification}
                                      onChange={(e) => handleKPIChange(sectionName, kIdx, 'means_of_verification', e.target.value)}
                                      placeholder="How will this be measured..."
                                    />
                                  </div>
                                </div>

                                {/* Remove item button — only when editable and more than 1 item */}
                                {!isReadOnly && section.items.length > 1 && (
                                  <div className="flex justify-end px-10 pt-5 pb-5 border-t border-gray-100">
                                    <button
                                      onClick={() => handleRemoveKPIItem(sectionName, kIdx)}
                                      className="flex items-center gap-4 text-[11px] font-bold text-red-500 hover:text-red-700 transition-colors"
                                    >
                                      <IconTrash size={13} />
                                      Remove
                                    </button>
                                  </div>
                                )}

                                {/* Self Assessment row — only visible after manager accepts */}
                                {isAccepted && (
                                  <div className="flex items-center justify-between gap-10 px-10 pb-10 border-t border-gray-100 pt-10 bg-blue-50/30">
                                    <div className="flex items-center gap-4">
                                      <label className="text-[10px] font-bold text-gray-500 uppercase whitespace-nowrap">
                                        Self Rating
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        max="3"
                                        step="1"
                                        readOnly={isManagerRated}
                                        className={`text-[14px] border border-border rounded px-4 h-[45px] w-[65px] text-center outline-none ${isManagerRated ? 'bg-bg-light text-text-secondary cursor-not-allowed' : 'bg-bg-dark text-text-primary focus:border-primary focus:ring-1 focus:ring-primary'}`}
                                        value={kpi.self_rating}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (val === '') { handleKPIChange(sectionName, kIdx, 'self_rating', ''); return; }
                                          const num = Number(val);
                                          if (num < 1 || num > 3) return;
                                          handleKPIChange(sectionName, kIdx, 'self_rating', val);
                                        }}
                                      />
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <label className="text-[10px] font-bold text-gray-500 uppercase whitespace-nowrap">
                                        Self Comment
                                      </label>
                                      <textarea
                                        readOnly={isManagerRated}
                                        className={`w-[400px] h-[60px] text-[12px] border border-border rounded p-3 resize-none outline-none ${isManagerRated ? 'bg-bg-light text-text-secondary cursor-not-allowed' : 'bg-bg-dark text-text-primary focus:border-primary focus:ring-1 focus:ring-primary'}`}
                                        value={kpi.self_comment}
                                        onChange={(e) => handleKPIChange(sectionName, kIdx, 'self_comment', e.target.value)}
                                        placeholder="Your self-assessment comment..."
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Add KPI Item button */}
                          {!isReadOnly && (
                            <div className="mt-5 mb-10">
                              <button
                                onClick={() => handleAddKPIItem(sectionName)}
                                className="flex items-center gap-5 px-10 py-5 border border-success text-success text-[11px] font-bold rounded hover:bg-green-50 transition-colors"
                              >
                                <IconPlus size={12} />
                                Add KPI
                              </button>
                            </div>
                          )}

                          {/* Section Actions */}
                          {isSubmitted && (
                            <div className="flex justify-end mt-10">
                              <span className="text-[11px] font-bold px-10 py-5 rounded bg-blue-50 text-blue-600">
                                Submitted — awaiting manager review
                              </span>
                            </div>
                          )}
                          {!isReadOnly && (
                            <div className="flex items-center justify-between mt-10">
                              {(() => {
                                const totalWeight = section.items.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
                                const isValid = totalWeight === 100;
                                return (
                                  <span className={`text-[11px] font-bold px-10 py-5 rounded ${isValid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                                    Total weight: {totalWeight}/100{!isValid && ' — must equal 100'}
                                  </span>
                                );
                              })()}
                              <button
                                onClick={() => handleSaveSection(sectionName)}
                                disabled={createSection.isPending || updateSection.isPending || noCycleSelected || section.items.reduce((s, i) => s + (Number(i.weight) || 0), 0) !== 100}
                                title={noCycleSelected ? 'Select a cycle and set start/end dates first' : ''}
                                className="px-15 py-3 bg-success text-white text-[11px] font-bold rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isSaved ? 'Update Section' : 'Save Section'}
                              </button>
                            </div>
                          )}
                          {isAccepted && (
                            <div className="flex justify-end mt-10">
                              <button
                                onClick={() => handleSaveSection(sectionName)}
                                disabled={selfRateSection.isPending || isManagerRated}
                                title={isManagerRated ? 'Manager has already rated this section' : ''}
                                className="px-15 py-3 bg-primary text-white text-[11px] font-bold rounded hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {selfRateSection.isPending ? 'Saving...' : 'Save Self Assessment'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom Actions */}
          {!allSectionsAccepted && (
            <div className="px-20 py-15 border-t border-border bg-gray-50/50 flex justify-end gap-10">
              {noCycleSelected && (
                <span className="text-[11px] text-amber-600 font-medium self-center">
                  Select a cycle and set start/end dates to enable saving
                </span>
              )}
              {!noCycleSelected && !allSectionsSaved && (
                <span className="text-[11px] text-amber-600 font-medium self-center">
                  Save all sections before submitting
                </span>
              )}
              <button
                onClick={handleSubmitAll}
                disabled={submitAll.isPending || noCycleSelected || !allSectionsSaved}
                title={
                  noCycleSelected
                    ? 'Select a cycle and set start/end dates first'
                    : !allSectionsSaved
                      ? 'Save all sections before submitting'
                      : ''
                }
                className="flex items-center gap-8 px-20 py-10 rounded bg-primary text-white text-[12px] font-bold uppercase tracking-wider hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconSend size={16} />
                Submit All Sections
              </button>
            </div>
          )}
        </div>
      )}

      {/* Manager View: Pending Reviews */}
      {activeTab === 'manager' && (userRole === 'manager' || userRole === 'both') && (
        <div className="bg-white rounded-lg border border-border overflow-hidden shadow-sm">
          <div className="px-20 py-15 border-b border-border bg-gray-50/50">
            <h3 className="text-lg font-bold text-gray-800">Pending Reviews from Direct Reports</h3>
            <p className="text-xs text-gray-600 mt-2">Review and approve/reject KPI sections submitted by your team</p>
          </div>

          <div className="divide-y divide-gray-100">
            {pendingReviewsData && pendingReviewsData.length > 0 ? (
              pendingReviewsData.map((review, idx) => (
                <div key={idx} className="p-20">
                  <div className="flex justify-between items-start mb-15">
                    <div>
                      <h4 className="text-sm font-bold text-gray-800">{review.employee_name || 'Employee'}</h4>
                      <p className="text-xs text-gray-600">{review.section_name}</p>
                      <span className={`inline-block mt-5 text-[11px] font-bold px-10 py-4 rounded ${getStatusColor(review.status)}`}>
                        {review.status?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex gap-10">
                      <button
                        onClick={() => handleAcceptSection(review)}
                        disabled={reviewSection.isPending}
                        className="flex items-center gap-5 px-12 py-6 bg-success text-white text-xs font-bold rounded hover:bg-green-600 transition-colors disabled:opacity-50"
                      >
                        <IconCheck size={14} />
                        Accept
                      </button>
                      <button
                        onClick={() => handleRejectSection(review)}
                        disabled={reviewSection.isPending}
                        className="flex items-center gap-5 px-12 py-6 bg-danger text-white text-xs font-bold rounded hover:bg-red-600 transition-colors disabled:opacity-50"
                      >
                        <IconX size={14} />
                        Reject
                      </button>
                    </div>
                  </div>

                  {review.items && review.items.length > 0 && (
                    <div className="bg-gray-50 p-15 rounded space-y-10">
                      {review.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="text-xs">
                          <p className="font-bold text-gray-800">{item.title}</p>
                          <p className="text-gray-600 mt-2">{item.description}</p>
                          <p className="text-gray-500 mt-2 italic">Verification: {item.means_of_verification}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-40 text-center text-gray-500">
                <p className="text-sm">No pending reviews at this time</p>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={confirmSubmitModalOpen}
        onClose={() => setConfirmSubmitModalOpen(false)}
        onConfirm={confirmSubmitAll}
        title="Submit All KPIs"
        message="Are you sure you want to submit all saved KPI sections? Once submitted, you won't be able to edit them until your manager reviews them."
        isLoading={submitAll.isPending}
        confirmLabel="Submit"
        loadingLabel="Submitting..."
        confirmClassName="btn btn-primary flex-1 flex items-center justify-center gap-5"
        headerIcon={<IconSend size={32} className="text-primary" />}
        headerIconBg="bg-primary/10"
      />
    </div>
  );
}
